import * as React from 'react';
import {
  ChevronDown,
  Download,
  MessageSquare,
  User as UserIcon,
} from 'lucide-react';
import type { Inquiry, InquiryListQuery, InquiryListResponse } from '@proptr/shared';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderFilter,
  HeaderLabel,
  SortHeader,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

type QualificationKey = NonNullable<InquiryListQuery['qualification']>;
type SortKey = 'createdAt' | 'name';
type SortOrder = 'asc' | 'desc';

const QUALIFICATION_OPTIONS: { value: QualificationKey; label: string }[] = [
  { value: 'all', label: 'All inquiries' },
  { value: 'relevant', label: 'Relevant inquiries' },
  { value: 'double-qualified', label: 'Double-qualified' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'pre-qualified', label: 'Pre-qualified' },
  { value: 'unqualified', label: 'Unqualified' },
];

export function InquiriesPage() {
  const [qualification, setQualification] = React.useState<QualificationKey>(
    'all',
  );
  const [sortBy, setSortBy] = React.useState<SortKey>('createdAt');
  const [order, setOrder] = React.useState<SortOrder>('desc');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [data, setData] = React.useState<InquiryListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await client.inquiries.exportCsv({
        qualification,
        sort: sortBy,
        order,
      });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inquiries-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.inquiries
      .list({ qualification, sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [qualification, sortBy, order, page, pageSize]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setOrder('desc');
    }
  };

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Inquiries
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              <span className="text-foreground">{meta?.all ?? '—'}</span> total
              {meta && (
                <>
                  {' · '}
                  <span className="text-foreground">
                    {meta.sources.listing}
                  </span>{' '}
                  listing
                  {' · '}
                  <span className="text-foreground">
                    {meta.sources.button}
                  </span>{' '}
                  button
                </>
              )}
            </p>
            {exportError && (
              <p className="mt-2 font-mono text-[11px] text-destructive">
                {exportError}
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
          >
            <Download />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>

        <TableCard
          minWidth={1200}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          <TableHeaderRow>
            {/* min-w folds in the rows' chevron+avatar columns
                (32+40+two 16px gaps) so labels still align while the
                filter chip sits flush left on every page. */}
            <HeaderFilter
              className="min-w-[304px] flex-[2.5_1_304px]"
              value={qualification}
              options={QUALIFICATION_OPTIONS}
              onChange={(v) => {
                setQualification(v);
                setPage(1);
              }}
            />
            <HeaderLabel className="min-w-[140px] flex-[1.5_1_140px]">Channel</HeaderLabel>
            <HeaderLabel className="min-w-[180px] flex-[2_1_180px]">Email</HeaderLabel>
            <HeaderLabel className="min-w-[160px] flex-[2_1_160px]">Source</HeaderLabel>
            <HeaderLabel className="min-w-[80px] flex-[1_1_80px]">Form Q&amp;A</HeaderLabel>
            <SortHeader
              label="Created"
              sortKey="createdAt"
              sortBy={sortBy}
              order={order}
              onSort={handleSort}
              className="min-w-[120px] flex-[1.5_1_120px]"
            />
          </TableHeaderRow>

          {error ? (
            <TableMessage tone="destructive">{error}</TableMessage>
          ) : items === null ? (
            <TableLoading />
          ) : items.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title="No inquiries found"
              description="No inquiries match the selected filters. New inquiries from your proptrees — listing clicks, forms, WhatsApp — land here as they come in."
            />
          ) : (
            items.map((inquiry) => (
              <InquiryRow key={inquiry._id} inquiry={inquiry} />
            ))
          )}
        </TableCard>
      </div>
    </AppShell>
  );
}

function InquiryRow({ inquiry }: { inquiry: Inquiry }) {
  const [open, setOpen] = React.useState(false);

  const clientName = inquiry.client?.name || '—';
  const clientPhone = inquiry.client?.phone || '—';
  const clientEmail = inquiry.client?.email || '—';
  const rawListingName = inquiry.listing?.name || inquiry.listing?.projectName;
  const rawListingRef = inquiry.listing?.externalId;
  const isButtonInquiry =
    isButtonPlaceholder(rawListingName) ||
    isButtonPlaceholder(rawListingRef) ||
    isButtonPlaceholder(inquiry.listingExternalId);
  const listingLabel = isButtonInquiry
    ? 'Button inquiry'
    : rawListingName;
  const listingRef = isButtonInquiry ? undefined : rawListingRef;
  const answered = countAnswered(inquiry);
  const totalQ = inquiry.formQuestionCount ?? 0;
  const createdAt =
    inquiry.createdAt && typeof inquiry.createdAt === 'string'
      ? inquiry.createdAt.slice(0, 10)
      : '—';

  return (
    <div
      className={cn(
        'border-b border-[hsl(var(--hairline))] last:border-b-0',
        open && 'bg-muted/20',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform',
              open && 'rotate-180',
            )}
          />
        </span>
        <ClientAvatar name={clientName} image={inquiry.client?.image} />
        <div className="min-w-[200px] flex-[2.5_1_200px]">
          <div className="truncate font-sans text-[14px] font-medium text-foreground">
            {clientName}
          </div>
          <div className="truncate font-mono text-[11px] text-muted-foreground">
            {clientPhone}
          </div>
        </div>
        <div className="min-w-[140px] flex-[1.5_1_140px]">
          <ChannelBadge channel={inquiry.channel} />
        </div>
        <RowCell
          className="min-w-[180px] flex-[2_1_180px]"
          value={clientEmail}
          dim={clientEmail === '—' || clientEmail === ''}
        />
        <div className="min-w-[160px] flex-[2_1_160px]">
          {listingLabel || listingRef ? (
            <>
              <div className="truncate font-sans text-[13px] font-medium text-foreground">
                {listingLabel || '—'}
              </div>
              {listingRef && (
                <div className="truncate mono-label text-muted-foreground">
                  {listingRef}
                </div>
              )}
            </>
          ) : (
            <div className="font-mono text-[12px] text-muted-foreground">—</div>
          )}
        </div>
        <div className="min-w-[80px] flex-[1_1_80px]">
          <div
            className={cn(
              'font-mono text-[13px] font-medium tabular-nums',
              totalQ === 0 && 'text-muted-foreground',
            )}
          >
            {totalQ > 0 ? `${answered}/${totalQ}` : '—'}
          </div>
        </div>
        <div className="min-w-[120px] flex-[1.5_1_120px]">
          <div className="font-mono text-[12px] text-foreground/80 tabular-nums">
            {createdAt}
          </div>
        </div>
      </button>
      {open && <InquiryDetail inquiry={inquiry} />}
    </div>
  );
}

function RowCell({
  value,
  className,
  dim,
}: {
  value: string;
  className?: string;
  dim?: boolean;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div
        className={cn(
          'truncate font-sans text-[13px] font-medium text-foreground',
          dim && 'text-muted-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function countAnswered(inquiry: Inquiry): number {
  const raw = inquiry.answeredQuestions;
  if (!raw || typeof raw !== 'object' || !inquiry.formId) return 0;
  const formBucket = (raw as Record<string, unknown>)[inquiry.formId];
  if (!formBucket || typeof formBucket !== 'object') return 0;
  return Object.keys(formBucket as Record<string, unknown>).filter(
    (k) => k !== 'completed' && k !== 'started',
  ).length;
}

function InquiryDetail({ inquiry }: { inquiry: Inquiry }) {
  const answers = extractAnswers(inquiry);
  const origin =
    inquiry.proptreeOrigin?.profileFirstName ||
    inquiry.proptreeOrigin?.alias ||
    inquiry.proptreeOrigin?.shortId;
  const rawListingName =
    inquiry.listing?.name || inquiry.listing?.projectName;
  const rawListingExt =
    inquiry.listing?.externalId || inquiry.listingExternalId;
  const isButtonInquiry =
    isButtonPlaceholder(rawListingName) || isButtonPlaceholder(rawListingExt);
  const sourceValue = isButtonInquiry
    ? 'Button inquiry'
    : rawListingName || rawListingExt;

  const leftRows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Name', value: inquiry.client?.name || '—' },
    { label: 'Email', value: inquiry.client?.email || '—' },
    { label: 'Phone', value: inquiry.client?.phone || '—' },
    { label: 'Channel', value: <ChannelBadge channel={inquiry.channel} /> },
  ];
  if (sourceValue) {
    leftRows.push({ label: 'Source', value: sourceValue });
  }
  if (inquiry.agent?.name) {
    leftRows.push({ label: 'Agent', value: inquiry.agent.name });
  }
  if (origin) {
    leftRows.push({ label: 'Source proptree', value: origin });
  }
  if (inquiry.type?.name) {
    leftRows.push({ label: 'Qualification', value: inquiry.type.name });
  }
  if (inquiry.rating?.name) {
    leftRows.push({ label: 'Rating', value: inquiry.rating.name });
  }
  if (inquiry.followUp?.name) {
    leftRows.push({ label: 'Follow-up', value: inquiry.followUp.name });
  }

  return (
    <div className="border-t border-[hsl(var(--hairline))] bg-muted/40 px-5 py-5">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Left: key/value rows */}
        <div className="divide-y divide-[hsl(var(--hairline))] overflow-hidden rounded-lg border border-[hsl(var(--hairline))] bg-card">
          {leftRows.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-[120px_1fr] gap-4 px-5 py-3.5"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                {r.label}
              </div>
              <div className="font-sans text-[14px] font-medium text-foreground">
                {r.value}
              </div>
            </div>
          ))}
        </div>

        {/* Right: form answers */}
        <div className="space-y-2.5">
          {answers.length > 0 ? (
            <>
              <div className="inline-flex rounded-md bg-primary/10 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
                Form answers
              </div>
              {answers.map((a) => (
                <AnswerCard key={a.id} answer={a} />
              ))}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-[hsl(var(--hairline))] bg-card px-5 py-6 font-mono text-[11px] text-muted-foreground">
              No form answers for this inquiry.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface NormalizedAnswer {
  id: string;
  question: string;
  answer: string;
  date?: string;
}

function extractAnswers(inquiry: Inquiry): NormalizedAnswer[] {
  const raw = inquiry.answeredQuestions;
  if (!raw || typeof raw !== 'object' || !inquiry.formId) return [];
  const outer = raw as Record<string, unknown>;
  const formBucket = outer[inquiry.formId];
  if (!formBucket || typeof formBucket !== 'object') return [];
  const items: NormalizedAnswer[] = [];
  for (const [key, val] of Object.entries(
    formBucket as Record<string, unknown>,
  )) {
    if (key === 'completed' || key === 'started') continue;
    if (!val || typeof val !== 'object') continue;
    const v = val as {
      id?: number | string;
      question?: string;
      answer?: unknown;
      date?: string;
    };
    items.push({
      id: String(v.id ?? key),
      question: v.question ?? '—',
      answer:
        typeof v.answer === 'string'
          ? v.answer
          : v.answer != null
            ? String(v.answer)
            : '—',
      date: v.date,
    });
  }
  items.sort((a, b) => {
    const ad = a.date ?? '';
    const bd = b.date ?? '';
    return ad.localeCompare(bd);
  });
  return items;
}

function AnswerCard({ answer }: { answer: NormalizedAnswer }) {
  const ts = answer.date ? formatTime(answer.date) : null;
  return (
    <div className="rounded-lg border border-[hsl(var(--hairline))] bg-primary/5 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="mono-label text-muted-foreground">Question</div>
        {ts && (
          <div className="font-mono text-[10px] text-muted-foreground">{ts}</div>
        )}
      </div>
      <div className="mt-1 font-sans text-[14px] font-medium text-primary">
        {answer.question}
      </div>
      <div className="mt-3 mono-label text-muted-foreground">Answer</div>
      <div className="mt-1 font-sans text-[14px] font-medium text-primary">
        {answer.answer}
      </div>
    </div>
  );
}

function isButtonPlaceholder(value: unknown): boolean {
  return typeof value === 'string' && value === '###BUTTON###';
}

function ChannelBadge({ channel }: { channel?: string | null }) {
  if (!channel) {
    return (
      <span className="font-sans text-[14px] font-medium text-muted-foreground">
        —
      </span>
    );
  }
  const label = channel.charAt(0).toUpperCase() + channel.slice(1);
  return (
    <span className="truncate font-sans text-[14px] font-medium text-foreground">
      {label}
    </span>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toISOString().slice(0, 10);
    const time = d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${date} ${time.toLowerCase()}`;
  } catch {
    return '';
  }
}

function ClientAvatar({
  name,
  image,
}: {
  name: string;
  image?: string | null;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  if (name && name !== '—') {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
        {initials(name)}
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
      <UserIcon className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function QualificationPill({
  value,
  label,
}: {
  value?: string | null;
  label?: string | null;
}) {
  if (!value) return <span className="pill pill-muted">—</span>;
  const cls =
    value === 'double-qualified'
      ? 'pill pill-success'
      : value === 'qualified'
        ? 'pill pill-accent'
        : value === 'pre-qualified'
          ? 'pill pill-warning'
          : value === 'unqualified'
            ? 'pill pill-muted'
            : 'pill pill-muted';
  return <span className={cls}>{label || value}</span>;
}

function FollowUpPill({ value }: { value?: string | null }) {
  if (value === 'followed_up')
    return <span className="pill pill-success">Done</span>;
  if (value === 'pending_follow_up')
    return <span className="pill pill-warning">Pending</span>;
  return <span className="pill pill-muted">Not yet</span>;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mono-label text-muted-foreground">{label}</div>
      <div className="mt-1 truncate font-mono text-[13px] font-semibold tabular-nums">
        {value}
      </div>
    </div>
  );
}
